import { LitElement, html, nothing } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import {
  getTimeline,
  getAgentMemories,
  getStaleMemories,
} from '../services/memory-api.js';
import type {
  MemoryAuditEntry,
  MemoryStats,
  MemoryTimelineResponse,
  MemoryAgentResponse,
  MemoryStaleResponse,
} from '../types/memory.js';

type ViewMode = 'timeline' | 'agent' | 'stale';
type ActionFilter = 'all' | 'read' | 'write';

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 86_400_000);
}

@customElement('cp-memory-timeline')
export class CpMemoryTimeline extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String, attribute: 'memory-ref' }) memoryRef = '';
  @property({ type: String, attribute: 'agent-id' }) agentId = '';
  @property({ type: Number, attribute: 'stale-days' }) staleDays = 90;

  @state() private _view: ViewMode = 'timeline';
  @state() private _actionFilter: ActionFilter = 'all';
  @state() private _refInput = '';
  @state() private _agentInput = '';
  @state() private _daysInput = 90;
  @state() private _loading = false;
  @state() private _error = '';
  @state() private _timelineData: MemoryTimelineResponse | null = null;
  @state() private _agentData: MemoryAgentResponse | null = null;
  @state() private _staleData: MemoryStaleResponse | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    if (this.memoryRef) {
      this._refInput = this.memoryRef;
      this._view = 'timeline';
      void this._loadTimeline();
    } else if (this.agentId) {
      this._agentInput = this.agentId;
      this._view = 'agent';
      void this._loadAgent();
    } else {
      this._daysInput = this.staleDays;
      this._view = 'stale';
      void this._loadStale();
    }
  }

  private async _loadTimeline(): Promise<void> {
    if (!this._refInput.trim()) {
      this._error = 'Memory-Ref erforderlich (z.B. "file:user_role.md")';
      return;
    }
    this._loading = true;
    this._error = '';
    try {
      this._timelineData = await getTimeline(this._refInput.trim(), 100);
    } catch (e) {
      this._error = (e as Error).message;
      this._timelineData = null;
    } finally {
      this._loading = false;
    }
  }

  private async _loadAgent(): Promise<void> {
    if (!this._agentInput.trim()) {
      this._error = 'Agent-ID erforderlich';
      return;
    }
    this._loading = true;
    this._error = '';
    try {
      this._agentData = await getAgentMemories(this._agentInput.trim(), { limit: 200 });
    } catch (e) {
      this._error = (e as Error).message;
      this._agentData = null;
    } finally {
      this._loading = false;
    }
  }

  private async _loadStale(): Promise<void> {
    this._loading = true;
    this._error = '';
    try {
      this._staleData = await getStaleMemories(this._daysInput);
    } catch (e) {
      this._error = (e as Error).message;
      this._staleData = null;
    } finally {
      this._loading = false;
    }
  }

  private _switchView(view: ViewMode): void {
    this._view = view;
    this._error = '';
    if (view === 'timeline' && this._refInput) void this._loadTimeline();
    if (view === 'agent' && this._agentInput) void this._loadAgent();
    if (view === 'stale') void this._loadStale();
  }

  render() {
    return html`
      <div class="cp-memory-timeline">
        <div class="cp-memory-timeline__header">
          <h3 class="cp-memory-timeline__title">Memory-Audit</h3>
          <div class="cp-memory-timeline__tabs">
            ${this._renderTab('timeline', 'Pro Memory')}
            ${this._renderTab('agent', 'Pro Agent')}
            ${this._renderTab('stale', 'Stale-Report')}
          </div>
          ${this._view !== 'stale' ? html`
            <div class="cp-memory-timeline__action-filter">
              ${this._renderActionFilterBtn('all', 'Alle')}
              ${this._renderActionFilterBtn('read', 'Read')}
              ${this._renderActionFilterBtn('write', 'Write')}
            </div>
          ` : nothing}
        </div>

        <div class="cp-memory-timeline__controls">
          ${this._renderControls()}
        </div>

        ${this._error
          ? html`<div class="cp-memory-timeline__error">${this._error}</div>`
          : nothing}

        ${this._loading
          ? html`<div class="cp-memory-timeline__loading">Laedt...</div>`
          : this._renderBody()}
      </div>
    `;
  }

  private _renderActionFilterBtn(filter: ActionFilter, label: string) {
    const active = this._actionFilter === filter;
    return html`
      <button
        class="cp-memory-timeline__action-btn ${active ? 'cp-memory-timeline__action-btn--active' : ''}"
        @click=${() => { this._actionFilter = filter; }}
      >${label}</button>
    `;
  }

  private _filterEntries(entries: import('../types/memory.js').MemoryAuditEntry[]) {
    if (this._actionFilter === 'all') return entries;
    if (this._actionFilter === 'read') return entries.filter(e => e.action === 'read');
    return entries.filter(e => e.action === 'write' || e.action === 'update' || e.action === 'delete');
  }

  private _renderTab(view: ViewMode, label: string) {
    const active = this._view === view;
    return html`
      <button
        class="cp-memory-timeline__tab ${active ? 'cp-memory-timeline__tab--active' : ''}"
        @click=${() => this._switchView(view)}
      >${label}</button>
    `;
  }

  private _renderControls() {
    if (this._view === 'timeline') {
      return html`
        <input
          class="cp-memory-timeline__input"
          type="text"
          placeholder='Memory-Ref (z.B. "file:user_role.md" oder "db:company_knowledge:104")'
          .value=${this._refInput}
          @input=${(e: Event) => { this._refInput = (e.target as HTMLInputElement).value; }}
          @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this._loadTimeline(); }}
        />
        <button class="cp-memory-timeline__btn" @click=${() => this._loadTimeline()}>Laden</button>
      `;
    }
    if (this._view === 'agent') {
      return html`
        <input
          class="cp-memory-timeline__input"
          type="text"
          placeholder="Agent-ID (z.B. lifecoach-karl)"
          .value=${this._agentInput}
          @input=${(e: Event) => { this._agentInput = (e.target as HTMLInputElement).value; }}
          @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this._loadAgent(); }}
        />
        <button class="cp-memory-timeline__btn" @click=${() => this._loadAgent()}>Laden</button>
      `;
    }
    return html`
      <label class="cp-memory-timeline__label">Stale ab (Tage):</label>
      <input
        class="cp-memory-timeline__input cp-memory-timeline__input--number"
        type="number"
        min="1"
        max="3650"
        .value=${String(this._daysInput)}
        @input=${(e: Event) => { this._daysInput = Number((e.target as HTMLInputElement).value) || 90; }}
        @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter') void this._loadStale(); }}
      />
      <button class="cp-memory-timeline__btn" @click=${() => this._loadStale()}>Aktualisieren</button>
    `;
  }

  private _renderBody() {
    if (this._view === 'timeline') return this._renderTimelineBody();
    if (this._view === 'agent') return this._renderAgentBody();
    return this._renderStaleBody();
  }

  private _renderTimelineBody() {
    const data = this._timelineData;
    if (!data) {
      return html`<div class="cp-memory-timeline__empty">Memory-Ref oben eingeben.</div>`;
    }
    const entries = this._filterEntries(data.entries);
    return html`
      ${data.stats ? this._renderStatsCard(data.stats) : nothing}
      ${entries.length === 0
        ? html`<div class="cp-memory-timeline__empty">Keine Audit-Eintraege fuer "${data.memoryRef}".</div>`
        : html`<div class="cp-memory-timeline__list">${entries.map(e => this._renderEntry(e, true))}</div>`}
    `;
  }

  private _renderAgentBody() {
    const data = this._agentData;
    if (!data) {
      return html`<div class="cp-memory-timeline__empty">Agent-ID oben eingeben.</div>`;
    }
    const entries = this._filterEntries(data.entries);
    if (entries.length === 0) {
      return html`<div class="cp-memory-timeline__empty">Keine Memory-Zugriffe fuer Agent "${data.agentId}".</div>`;
    }
    return html`<div class="cp-memory-timeline__list">${entries.map(e => this._renderEntry(e, false))}</div>`;
  }

  private _renderStaleBody() {
    const data = this._staleData;
    if (!data) return nothing;
    if (data.entries.length === 0) {
      return html`<div class="cp-memory-timeline__empty">Keine Memories aelter als ${data.days} Tage.</div>`;
    }
    return html`
      <div class="cp-memory-timeline__stale-meta">
        Cutoff: ${fmtDateTime(data.cutoff)} (${data.days} Tage). ${data.entries.length} Memory(s) als Deprecate-Kandidat.
      </div>
      <table class="cp-memory-timeline__table">
        <thead>
          <tr>
            <th>Memory-Ref</th>
            <th>Type</th>
            <th>Reads</th>
            <th>Writes</th>
            <th>Letzter Read</th>
            <th>Tage</th>
            <th>First Seen</th>
          </tr>
        </thead>
        <tbody>
          ${data.entries.map(s => html`
            <tr>
              <td class="cp-memory-timeline__cell-ref">${s.memory_ref}</td>
              <td>${s.memory_type ?? '—'}</td>
              <td>${s.read_count}</td>
              <td>${s.write_count}</td>
              <td>${fmtDateTime(s.last_read_at)}</td>
              <td class="cp-memory-timeline__cell-days">${daysSince(s.last_read_at ?? s.first_seen_at) ?? '—'}</td>
              <td>${fmtDateTime(s.first_seen_at)}</td>
            </tr>
          `)}
        </tbody>
      </table>
    `;
  }

  private _renderStatsCard(s: MemoryStats) {
    return html`
      <div class="cp-memory-timeline__stats">
        <div><span class="cp-memory-timeline__stats-label">Reads</span> <strong>${s.read_count}</strong></div>
        <div><span class="cp-memory-timeline__stats-label">Writes</span> <strong>${s.write_count}</strong></div>
        <div><span class="cp-memory-timeline__stats-label">Letzter Read</span> ${fmtDateTime(s.last_read_at)}</div>
        <div><span class="cp-memory-timeline__stats-label">Letzter Write</span> ${fmtDateTime(s.last_write_at)}</div>
        <div><span class="cp-memory-timeline__stats-label">First Seen</span> ${fmtDateTime(s.first_seen_at)}</div>
      </div>
    `;
  }

  private _renderEntry(e: MemoryAuditEntry, hideRef: boolean) {
    return html`
      <div class="cp-memory-timeline__entry">
        <span class="cp-memory-timeline__entry-time">${fmtDateTime(e.timestamp)}</span>
        <span class="cp-memory-timeline__entry-action cp-memory-timeline__entry-action--${e.action}">${e.action}</span>
        <span class="cp-memory-timeline__entry-outcome cp-memory-timeline__entry-outcome--${e.outcome}">${e.outcome}</span>
        <span class="cp-memory-timeline__entry-agent">${e.agent_id}</span>
        ${hideRef ? nothing : html`<span class="cp-memory-timeline__entry-ref">${e.memory_ref}</span>`}
        ${e.task_context ? html`<span class="cp-memory-timeline__entry-task">${e.task_context}</span>` : nothing}
        <span class="cp-memory-timeline__entry-duration">${e.duration_ms}ms</span>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-memory-timeline': CpMemoryTimeline;
  }
}
